import React, { useEffect, useState } from 'react';
import { Card, CardBody, Container, Button, Input} from "reactstrap";
import Breadcrumb from "Components/UI/Breadcrumb";
import PaginatedTable from "Components/Tables/PaginatedTable";
import DeleteModal from 'Components/Modals/DeleteModal';
import useTableFilters from 'helpers/hooks/useTableFilters';
import { generateQueryParams } from 'helpers/queryParams';
import { deletePostcode, getPostcodes, updatePostcodesBulk } from 'helpers/API Calls/System & Settings/Postcodes';
import Spinners from "Components/UI/Spinner";

interface DeliveryArea {
  id: number;
  region: string;
  town: string;
  postcode: string;
  charges: number;
}

const tableHead = [
  { id: 1, name: "#", isSortable: true, key: "id" },
  { id: 2, name: "Region", isSortable: true, key: "region" },
  { id: 3, name: "Town", isSortable: true, key: "town" },
  { id: 4, name: "Postcodes", isSortable: true, key: "postcode" },
  { id: 5, name: "Delivery Fee", isSortable: true, key: "charges" },
  { id: 6, name: "Actions", isSortable: false, key: "actions" }
];


function DeliveryPricing() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [deliveryAreas, setDeliveryAreas] = useState<DeliveryArea[]>([]);
  const [pagination, setPagination] = useState<any>({current_page: 1, last_page: 1, per_page: 10, total: 0});

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  
  const [isBulkEditing, setIsBulkEditing] = useState(false);
  const [editedAreas, setEditedAreas] = useState<DeliveryArea[]>([]);
  
  const { filters, updateFilter } = useTableFilters();

  const fetchData = async () => {
    setIsLoading(true);
    const response = await getPostcodes(generateQueryParams(filters));
    if (response.success) {
      setDeliveryAreas(response.data.items);
      setEditedAreas([]);
      setPagination(response.data.pagination);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem) {
      const response = await deletePostcode(selectedItem.id);
      if (response.success) {
        setDeleteModal(false);
        setSelectedItem(null);
        fetchData();
      }
    }
  };

  const onCloseClick = () => {
    setDeleteModal(false);
    setSelectedItem(null);
  };

  const handleToggleBulkEdit = () => {
    setIsBulkEditing(prev => !prev);
  };

  const handleBulkInputChange = (id: number, field: keyof DeliveryArea, value: string | number) => {
    const updatedCharge = Number(value);
    const original = deliveryAreas.find(area => area.id === id);
    if (!original || original.charges === updatedCharge) {
      // Remove from editedAreas if change reverted or invalid
      setEditedAreas(prev => prev.filter(area => area.id !== id));
      return;
    }
  
    setEditedAreas(prev => {
      const existing = prev.find(area => area.id === id);
      if (existing) {
        return prev.map(area =>
          area.id === id ? { ...area, charges: updatedCharge } : area
        );
      } else {
        return [...prev, { ...original, charges: updatedCharge }];
      }
    });
  };

  const handleSaveAll = async () => {
    if (editedAreas.length === 0) return;
    const data = editedAreas.map(area => ({
      id: area.id, 
      charges: area.charges
    }))
    const response = await updatePostcodesBulk({postcodes : data});
    if (response.success) {
      fetchData();
      handleToggleBulkEdit();
      setEditedAreas([]);
    }
  };
  
  const handleCancelAll = () => {
    setIsBulkEditing(false);
    setEditedAreas([]);
  };

  return (
    <>
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Delivery Pricing" }]} />
        {isLoading ? <div className="position-relative vh-100"><Spinners/></div>
        :
        <Card>
          <CardBody>
            <PaginatedTable
              tableHead={tableHead}
              isAddButton={false}
              pagination={pagination}
              updateFilter={updateFilter}
              filters={filters}
              customButton={!isBulkEditing ? (
                <Button color="primary" onClick={handleToggleBulkEdit}>Edit All</Button>
              ) : (
                <>
                  <Button color="secondary" className='me-2' onClick={handleCancelAll}>Cancel</Button>
                  <Button color="success" onClick={handleSaveAll}>Save All</Button>
                </>
              )}
            >
              {!isLoading && deliveryAreas.length !== 0 && deliveryAreas.map((area) => (
                <tr key={area.id}>
                  <td>{area.id}</td>
                  <td>{area.region}</td>
                  <td>{area.town}</td>
                  <td>{area.postcode}</td>
                  <td>
                    {isBulkEditing ? (
                      <Input
                        type="number"
                        value={editedAreas.find(a => a.id === area.id)?.charges ?? area.charges}
                        onChange={(e) => handleBulkInputChange(area.id, "charges", e.target.value)}
                        min={0}
                        step={10}
                      />
                    ) : (
                      `$${area?.charges || 0}`
                    )}
                  </td>
                  <td>
                    <Button color="danger" size="sm" onClick={() => handleDeleteClick(area)} disabled={isBulkEditing}>
                      <i className="mdi mdi-trash-can-outline"></i>
                    </Button>
                  </td>
                </tr>
              ))}
              {!isLoading && deliveryAreas.length === 0 && <tr className="text-center"><td colSpan={tableHead.length} className="text-danger">No Data Found</td></tr>}
            </PaginatedTable>
          </CardBody>
        </Card>
        }
      </Container>
    </div>

    {deleteModal && (
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteConfirm}
        onCloseClick={onCloseClick}
        message="this delivery area."
      />
    )}
    </>
  );
}

export default DeliveryPricing;
